import { useI18N } from "common/hooks";
import type { VenueDataSourceValue } from "common/types";
import { getVenueDataSource, hasVenueData } from "common/utils";
import { formatDateShort } from "common/utils/general";

export type VenueDataSourceInput = {
  // Venue uses the plural name, VenueDetails the singular — accept either.
  reviewsDetailsSubmitted?: number;
  reviewDetailsSubmitted?: number;
  hasExperiences?: boolean;
  hasAlerts?: boolean;
  hasTeamTags?: boolean;
  hasGeneralUserTags?: boolean;
  hasGeneralUserPhotos?: boolean;
  venueDataSource?: VenueDataSourceValue;
  latestReviewDateUpdated?: string;
};

/**
 * Reconciles the two field namings before the shared resolver sees them.
 * Drop hasGeneralUserTags or venueDataSource and getVenueDataSource falls
 * through to its team-tag rule, so a claimed venue reads "Community
 * Contributed" and a mixed-contributor one "Sociability Verified".
 */
const dataSourceSignals = (venue: VenueDataSourceInput) => ({
  reviewsDetailsSubmitted:
    venue.reviewsDetailsSubmitted ?? venue.reviewDetailsSubmitted ?? 0,
  hasExperiences: venue.hasExperiences,
  hasAlerts: venue.hasAlerts,
  hasTeamTags: venue.hasTeamTags,
  hasGeneralUserTags: venue.hasGeneralUserTags,
  hasGeneralUserPhotos: venue.hasGeneralUserPhotos,
  venueDataSource: venue.venueDataSource,
});

/**
 * The provenance the badge displays, or "none" when it shows nothing. Every
 * reader goes through this, so the badge and the callers reasoning about it
 * can never disagree.
 */
const resolvedDataSource = (
  venue: VenueDataSourceInput | null | undefined,
): VenueDataSourceValue => {
  if (!venue) return "none";
  const signals = dataSourceSignals(venue);
  return hasVenueData(signals) ? getVenueDataSource(signals) : "none";
};

/** Whether the badge renders anything for this venue. */
export const hasVenueDataSourceBadge = (
  venue: VenueDataSourceInput | null | undefined,
): boolean => resolvedDataSource(venue) !== "none";

/** Whether the venue carries the "Sociability verified" badge. */
export const isVenueSociabilityVerified = (
  venue: VenueDataSourceInput | null | undefined,
): boolean => resolvedDataSource(venue) === "team";

export function VenueDataSourceBadge({
  venue,
  className = "flex items-center gap-1",
  iconClassName = "w-5 h-5 shrink-0",
  labelClassName = "label-m-semibold text-fg-neutral-subtle whitespace-nowrap",
}: {
  venue: VenueDataSourceInput;
  className?: string;
  iconClassName?: string;
  labelClassName?: string;
}) {
  const i18n = useI18N();
  const dataSource = resolvedDataSource(venue);
  if (dataSource === "none") return null;

  // Same team/user badge as VenueCard's "Variant indicators" — replaces the
  // old businessVerified text everywhere on the venue profile page.
  const BADGE = {
    team: {
      icon: "/images/sociability_verified.svg",
      label: i18n.get("sociabilityVerified"),
    },
    user: {
      icon: "/images/user_updated.svg",
      label: i18n.get("userUpdated"),
    },
  } as const;

  const lastUpdated = formatDateShort(venue.latestReviewDateUpdated || "");

  return (
    <div className={className}>
      <img
        src={BADGE[dataSource].icon}
        alt=""
        aria-hidden="true"
        className={iconClassName}
      />
      <span className={labelClassName}>
        {BADGE[dataSource].label}
        {lastUpdated ? `: ${lastUpdated}` : ""}
      </span>
    </div>
  );
}
